/**
 * FacialVisionAILogo — compact wordmark for the AI aesthetic mirror.
 *
 * Design intent: monoline eye-glyph fused with "FacialVisionAI" set in a
 * clean sans wordmark. Uses two accent colors (rose → violet) that
 * match the tile / lobby / portal touch points so the brand feels
 * consistent everywhere it appears.
 *
 * Usage:
 *   <FacialVisionAILogo />                          // default 140×32
 *   <FacialVisionAILogo size="lg" />                // 200×46
 *   <FacialVisionAILogo variant="mono-white" />     // for dark chrome
 *   <FacialVisionAILogo iconOnly />                 // just the eye-glyph
 */
import React from "react";

const SIZES = {
  sm: { w: 128, h: 24, fs: 12, iconR: 8,  gap: 6 },
  md: { w: 170, h: 32, fs: 16, iconR: 10, gap: 7 },
  lg: { w: 240, h: 46, fs: 23, iconR: 14, gap: 9 },
  xl: { w: 310, h: 60, fs: 30, iconR: 18, gap: 11 },
};

const VARIANTS = {
  color: {
    iconStroke: "url(#fva-grad)",
    iconAccent: "#f472b6",
    face: "#111827",
    vision: "#111827",
    ai: "url(#fva-grad)",
  },
  "mono-white": {
    iconStroke: "#ffffff",
    iconAccent: "#ffffff",
    face: "#ffffff",
    vision: "#ffffff",
    ai: "#ffffff",
  },
  "mono-black": {
    iconStroke: "#0f172a",
    iconAccent: "#0f172a",
    face: "#0f172a",
    vision: "#0f172a",
    ai: "#0f172a",
  },
};

export default function FacialVisionAILogo({
  size = "md",
  variant = "color",
  iconOnly = false,
  className = "",
  "data-testid": testId = "face-vision-ai-logo",
}) {
  const s = SIZES[size] || SIZES.md;
  const v = VARIANTS[variant] || VARIANTS.color;

  const iconW = s.h; // square icon
  const wordX = iconW + s.gap;
  const totalW = iconOnly ? iconW : s.w;

  const strokeW = Math.max(1.4, s.h * 0.06);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${totalW} ${s.h}`}
      width={totalW}
      height={s.h}
      role="img"
      aria-label="FacialVisionAI"
      className={className}
      data-testid={testId}
    >
      <defs>
        <linearGradient id="fva-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f472b6" />
          <stop offset="55%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>

      {/* Eye-glyph — a lidded eye with an inner iris/pupil composed as
          two concentric arcs. Simple enough to read at 24px, elegant
          enough to feel editorial at 60px. */}
      <g transform={`translate(${s.h / 2} ${s.h / 2})`}>
        {/* outer lid */}
        <path
          d={`M -${s.iconR} 0 Q 0 -${s.iconR * 0.95} ${s.iconR} 0 Q 0 ${s.iconR * 0.95} -${s.iconR} 0 Z`}
          fill="none"
          stroke={v.iconStroke}
          strokeWidth={strokeW}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* iris */}
        <circle cx="0" cy="0" r={s.iconR * 0.42} fill={v.iconAccent} />
        {/* pupil highlight */}
        <circle cx={s.iconR * 0.12} cy={-s.iconR * 0.12} r={s.iconR * 0.12} fill="#ffffff" opacity="0.9" />
      </g>

      {!iconOnly && (
        <g
          transform={`translate(${wordX} ${s.h / 2})`}
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          <text
            x="0"
            y="0"
            dominantBaseline="middle"
            fontSize={s.fs}
            fontWeight="700"
            letterSpacing="-0.5"
            fill={v.face}
          >
            Facial
          </text>
          <text
            x={s.fs * 3.15}
            y="0"
            dominantBaseline="middle"
            fontSize={s.fs}
            fontWeight="400"
            fontStyle="italic"
            letterSpacing="-0.3"
            fill={v.vision}
          >
            Vision
          </text>
          <text
            x={s.fs * 6.6}
            y="0"
            dominantBaseline="middle"
            fontSize={s.fs * 0.72}
            fontWeight="900"
            letterSpacing="1.5"
            fill={v.ai}
            style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
          >
            AI
          </text>
        </g>
      )}
    </svg>
  );
}
